import * as React from 'react';
import ICoordinate from '../../../../map/ICoordinate';
import MapService from '../../../../map/MapService';
import { PropertyAddress } from '../types';

const LOOKUP_DELAY_IN_MILLISECONDS = 700;

export type AddressLocation =
    | { status: 'incomplete' }
    | { status: 'unavailable' }
    | { status: 'searching' }
    | { status: 'not-found' }
    | { status: 'found'; location: ICoordinate }
    | { status: 'picked'; location: ICoordinate };

type Lookup = {
    searchText: string;
    location: ICoordinate | null;
};

export function isGoogleMapsAvailable(): boolean {
    return Boolean(window.google && window.google.maps);
}

export function isLocationSettled(addressLocation: AddressLocation): boolean {
    return (
        addressLocation.status === 'found' ||
        addressLocation.status === 'picked' ||
        addressLocation.status === 'unavailable'
    );
}

export function useAddressLocation({
    addressLine1,
    addressPostalNumber,
    addressCity,
    addressPointLocation
}: PropertyAddress): AddressLocation {
    let [lookup, setLookup] = React.useState<Lookup | null>(null);
    let isComplete = [addressLine1, addressPostalNumber, addressCity].every(
        (part) => part.trim().length > 0
    );
    let searchText = [addressLine1, addressPostalNumber, addressCity].join(' ');

    React.useEffect(() => {
        if (!isComplete || !isGoogleMapsAvailable()) {
            return undefined;
        }

        let isCurrent = true;
        let timer = setTimeout(() => {
            MapService.getAddress(addressLine1, addressPostalNumber, addressCity)
                .then((result) => {
                    if (isCurrent) {
                        setLookup({
                            searchText,
                            location: result
                                ? {
                                      latitude: result.geometry.location.lat(),
                                      longitude: result.geometry.location.lng()
                                  }
                                : null
                        });
                    }
                })
                .catch(() => isCurrent && setLookup({ searchText, location: null }));
        }, LOOKUP_DELAY_IN_MILLISECONDS);

        return () => {
            isCurrent = false;
            clearTimeout(timer);
        };
    }, [isComplete, searchText, addressLine1, addressPostalNumber, addressCity]);

    if (addressPointLocation) {
        return { status: 'picked', location: addressPointLocation };
    }
    if (!isGoogleMapsAvailable()) {
        return { status: 'unavailable' };
    }
    if (!isComplete) {
        return { status: 'incomplete' };
    }
    if (!lookup || lookup.searchText !== searchText) {
        return { status: 'searching' };
    }
    return lookup.location
        ? { status: 'found', location: lookup.location }
        : { status: 'not-found' };
}
